import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, RotateCcw, Mic, Phone, PhoneIncoming, PhoneOutgoing, 
  FileText, Database, BarChart3, Plug, Users, Clock, HardDrive, Zap,
  Volume2, Settings2, Palette, Check, X, Loader2, ChevronDown
} from 'lucide-react';
import { clientSettingsAPI } from '../../services/api';

const ClientSettingsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState(null);
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('features');

  const fetchClientSettings = useCallback(async () => {
    try {
      const [settingsRes, presetsRes] = await Promise.all([
        clientSettingsAPI.getSettings(clientId),
        clientSettingsAPI.getPresets()
      ]);
      
      setClient({
        id: clientId,
        name: settingsRes.data.client_name
      });
      setSettings(settingsRes.data.settings);
      setPresets(presetsRes.data.presets || []);
      setSelectedPreset(settingsRes.data.applied_preset || '');
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load client settings');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientSettings();
  }, [fetchClientSettings]);

  const handleFeatureToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: !prev.features[key]
      }
    }));
    setSelectedPreset(''); // Clear preset when manually editing
  };

  const handleLimitChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [key]: parseInt(value) || 0
      }
    }));
    setSelectedPreset('');
  };

  const handleVoiceSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      voice_settings: {
        ...prev.voice_settings,
        [key]: value
      }
    }));
    setSelectedPreset('');
  };

  const handlePitchRangeChange = (bound, value) => {
    setSettings(prev => ({
      ...prev,
      voice_settings: {
        ...prev.voice_settings,
        pitch_range: {
          ...prev.voice_settings.pitch_range,
          [bound]: parseFloat(value) || 1.0
        }
      }
    }));
    setSelectedPreset('');
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      await clientSettingsAPI.updateSettings(clientId, settings);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPreset = async (presetId) => {
    if (!presetId) return;
    
    setSaving(true);
    setError('');
    try {
      const response = await clientSettingsAPI.applyPreset(clientId, presetId);
      setSettings(response.data.settings);
      setSelectedPreset(response.data.preset_name);
      setSuccess(`Preset "${response.data.preset_name}" applied successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error applying preset:', err);
      setError(err.response?.data?.detail || 'Failed to apply preset');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) return;
    
    setSaving(true);
    try {
      const response = await clientSettingsAPI.resetSettings(clientId);
      setSettings(response.data.settings);
      setSelectedPreset('');
      setSuccess('Settings reset to defaults');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting settings:', err);
      setError('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  const featureGroups = [
    {
      title: 'Call Features',
      icon: Phone,
      features: [
        { key: 'inbound_calls', label: 'Inbound Calls', desc: 'Receive incoming calls', icon: PhoneIncoming },
        { key: 'outbound_calls', label: 'Outbound Calls', desc: 'Make outgoing calls', icon: PhoneOutgoing },
        { key: 'call_transfer', label: 'Call Transfer', desc: 'Transfer calls to humans', icon: Phone },
        { key: 'voice_recordings', label: 'Voice Recordings', desc: 'Record calls for review', icon: Mic }
      ]
    },
    {
      title: 'Agent Features',
      icon: Settings2,
      features: [
        { key: 'script_builder', label: 'Script Builder', desc: 'Multi-step conversation scripts', icon: FileText },
        { key: 'knowledge_base', label: 'Knowledge Base', desc: 'Upload documents for AI context', icon: Database },
        { key: 'custom_greetings', label: 'Custom Greetings', desc: 'Personalize agent greetings', icon: Volume2 },
        { key: 'voice_selection', label: 'Voice Selection', desc: 'Choose different AI voices', icon: Mic }
      ]
    },
    {
      title: 'Analytics & Integrations',
      icon: BarChart3,
      features: [
        { key: 'analytics_dashboard', label: 'Analytics Dashboard', desc: 'View call metrics & reports', icon: BarChart3 },
        { key: 'real_time_monitoring', label: 'Real-time Monitoring', desc: 'Live call dashboard', icon: Zap },
        { key: 'integrations_marketplace', label: 'Integrations', desc: 'Third-party integrations', icon: Plug },
        { key: 'bulk_export', label: 'Bulk Export', desc: 'Export call data/transcripts', icon: FileText }
      ]
    },
    {
      title: 'Advanced',
      icon: Palette,
      features: [
        { key: 'api_access', label: 'API Access', desc: 'Programmatic API access', icon: Zap },
        { key: 'white_labeling', label: 'White Labeling', desc: 'Custom branding options', icon: Palette }
      ]
    }
  ];

  const limitFields = [
    { key: 'max_voice_agents', label: 'Max Voice Agents', icon: Mic, unit: 'agents' },
    { key: 'max_phone_numbers', label: 'Max Phone Numbers', icon: Phone, unit: 'numbers' },
    { key: 'max_end_users', label: 'Max End Users', icon: Users, unit: 'users' },
    { key: 'knowledge_base_size_mb', label: 'Knowledge Base Size', icon: HardDrive, unit: 'MB' },
    { key: 'max_call_duration_minutes', label: 'Max Call Duration', icon: Clock, unit: 'minutes' },
    { key: 'monthly_minutes', label: 'Monthly Minutes', icon: Clock, unit: 'minutes' },
    { key: 'max_concurrent_calls', label: 'Concurrent Calls', icon: Phone, unit: 'calls' }
  ];

  const availableVoices = ['nova', 'alloy', 'echo', 'fable', 'onyx', 'shimmer'];

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="client-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/clients')}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Client Settings</h1>
            <p className="text-gray-400">{client?.name || 'Loading...'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResetToDefaults}
            disabled={saving}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black px-4 py-2 rounded-lg font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 flex justify-between">
          {error}
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Plan Preset Selector */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Quick Apply Plan Preset</h3>
            <p className="text-gray-400 text-sm">Apply a predefined set of features and limits</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                value=""
                onChange={(e) => handleApplyPreset(e.target.value)}
                className="appearance-none bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-10 text-white outline-none focus:border-teal-400 min-w-[200px]"
              >
                <option value="">Select a preset...</option>
                {presets.map(preset => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name} {preset.is_builtin ? '(Built-in)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {selectedPreset && (
              <span className="px-3 py-1 bg-teal-400/10 text-teal-400 rounded-full text-sm">
                Active: {selectedPreset}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-2">
        {[
          { id: 'features', label: 'Feature Toggles', icon: Settings2 },
          { id: 'limits', label: 'Limits & Quotas', icon: BarChart3 },
          { id: 'voice', label: 'Voice Settings', icon: Volume2 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-teal-400/10 text-teal-400'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Feature Toggles Tab */}
      {activeTab === 'features' && settings && (
        <div className="space-y-6">
          {featureGroups.map(group => (
            <div key={group.title} className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <group.icon className="w-5 h-5 text-teal-400" />
                <h3 className="text-lg font-semibold text-white">{group.title}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.features.map(feature => (
                  <div
                    key={feature.key}
                    className="flex items-center justify-between p-4 bg-[#1a1a1f] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <feature.icon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-white font-medium">{feature.label}</p>
                        <p className="text-gray-500 text-sm">{feature.desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleFeatureToggle(feature.key)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        settings.features[feature.key] ? 'bg-teal-400' : 'bg-gray-700'
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          settings.features[feature.key] ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Limits Tab */}
      {activeTab === 'limits' && settings && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Resource Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {limitFields.map(field => (
              <div key={field.key} className="space-y-2">
                <label className="flex items-center gap-2 text-gray-300">
                  <field.icon className="w-4 h-4 text-gray-500" />
                  {field.label}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={settings.limits[field.key] || 0}
                    onChange={(e) => handleLimitChange(field.key, e.target.value)}
                    min="0"
                    className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                  <span className="text-gray-500 text-sm w-20">{field.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice Settings Tab */}
      {activeTab === 'voice' && settings && (
        <div className="space-y-6">
          {/* Default Voice */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Default Voice</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {availableVoices.map(voice => (
                <button
                  key={voice}
                  onClick={() => handleVoiceSettingChange('default_voice', voice)}
                  className={`p-3 rounded-lg border transition-colors ${
                    settings.voice_settings.default_voice === voice
                      ? 'border-teal-400 bg-teal-400/10 text-teal-400'
                      : 'border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <Mic className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm capitalize">{voice}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Allowed Voices */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Allowed Voices</h3>
            <p className="text-gray-400 text-sm mb-4">Select which voices this client can use</p>
            <div className="flex flex-wrap gap-3">
              {availableVoices.map(voice => {
                const isAllowed = settings.voice_settings.allowed_voices?.includes(voice);
                return (
                  <button
                    key={voice}
                    onClick={() => {
                      const current = settings.voice_settings.allowed_voices || [];
                      const updated = isAllowed
                        ? current.filter(v => v !== voice)
                        : [...current, voice];
                      handleVoiceSettingChange('allowed_voices', updated);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      isAllowed
                        ? 'border-teal-400 bg-teal-400/10 text-teal-400'
                        : 'border-gray-700 text-gray-500 hover:border-gray-600'
                    }`}
                  >
                    {isAllowed ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    <span className="capitalize">{voice}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pitch Adjustment */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Pitch Adjustment</h3>
                <p className="text-gray-400 text-sm">Allow clients to modify voice pitch</p>
              </div>
              <button
                onClick={() => handleVoiceSettingChange('pitch_adjustment_enabled', !settings.voice_settings.pitch_adjustment_enabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.voice_settings.pitch_adjustment_enabled ? 'bg-teal-400' : 'bg-gray-700'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    settings.voice_settings.pitch_adjustment_enabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
            
            {settings.voice_settings.pitch_adjustment_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-800">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Min Pitch</label>
                  <input
                    type="number"
                    value={settings.voice_settings.pitch_range?.min || 0.5}
                    onChange={(e) => handlePitchRangeChange('min', e.target.value)}
                    step="0.1"
                    min="0.25"
                    max="4"
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Max Pitch</label>
                  <input
                    type="number"
                    value={settings.voice_settings.pitch_range?.max || 2.0}
                    onChange={(e) => handlePitchRangeChange('max', e.target.value)}
                    step="0.1"
                    min="0.25"
                    max="4"
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Default Pitch</label>
                  <input
                    type="number"
                    value={settings.voice_settings.default_pitch || 1.0}
                    onChange={(e) => handleVoiceSettingChange('default_pitch', parseFloat(e.target.value))}
                    step="0.1"
                    min="0.25"
                    max="4"
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-teal-400"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Speed Adjustment */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Speed Adjustment</h3>
                <p className="text-gray-400 text-sm">Allow clients to modify voice speed</p>
              </div>
              <button
                onClick={() => handleVoiceSettingChange('speed_adjustment_enabled', !settings.voice_settings.speed_adjustment_enabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.voice_settings.speed_adjustment_enabled ? 'bg-teal-400' : 'bg-gray-700'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    settings.voice_settings.speed_adjustment_enabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSettingsPage;
